// src/components/Creditos.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  CreditCard, DollarSign, Clock, CheckCircle2, X, Save,
  TrendingUp, AlertCircle, Search, Filter, Eye, ArrowUpRight, ArrowDownRight,
  BarChart3, Users, Landmark, Calendar, ChevronRight, RefreshCw
} from 'lucide-react';
import API_BASE_URL from '../Config';

// Utilidades
const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', maximumFractionDigits: 0 })
    .format(Number(amount || 0));

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString('es-PY', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Creditos() {
  // Data desde Back
  const [stats, setStats] = useState(null);
  const [tarjetas, setTarjetas] = useState([]);
  const [prestamos, setPrestamos] = useState([]);

  // Loading / error
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingPrestamos, setLoadingPrestamos] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Filtros de cartera
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [searchTerm, setSearchTerm] = useState('');

  // Detalle modal
  const [selectedPrestamo, setSelectedPrestamo] = useState(null);

  // Fetchers
  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/prestamos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Stats: ${res.status}`);
      const data = await res.json();
      const lista = Array.isArray(data) ? data : [];

      // Computar estadísticas de la data real
      const activos = lista.filter(p => (p.estado || '').toUpperCase() === 'ACTIVO');
      const enMora = lista.filter(p => (p.estado || '').toUpperCase() === 'EN_MORA');
      const totalCartera = activos.reduce((s, p) => s + (p.saldoPendiente || 0), 0);
      const totalOriginal = activos.reduce((s, p) => s + (p.montoOriginal || 0), 0);

      setStats({
        prestamosActivos: activos.length,
        totalCartera,
        totalOriginal,
        enMora: enMora.length,
        totalPrestamos: lista.length,
      });
      setPrestamos(lista);
    } catch (e) {
      setErrorMsg('No se pudieron cargar los datos de cartera.');
      console.error(e);
    } finally {
      setLoadingStats(false);
      setLoadingPrestamos(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Filtrado de préstamos
  const prestamosFiltrados = useMemo(() => {
    let lista = prestamos;
    if (filtroEstado !== 'TODOS') {
      lista = lista.filter(p => (p.estado || '').toUpperCase() === filtroEstado);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      lista = lista.filter(p => {
        const nombre = (p.socioNombre || p.socioNombreCompleto || '').toLowerCase();
        const id = String(p.idPrestamo || p.id || '');
        return nombre.includes(q) || id.includes(q);
      });
    }
    return lista;
  }, [prestamos, filtroEstado, searchTerm]);

  // Cards de estadísticas
  const statsCards = useMemo(() => {
    const s = stats || { prestamosActivos: 0, totalCartera: 0, enMora: 0, totalPrestamos: 0 };
    return [
      {
        key: 'activos',
        titulo: 'Préstamos Activos',
        valor: s.prestamosActivos,
        icon: Landmark,
        gradient: 'from-blue-500 to-blue-600',
        iconBg: 'bg-blue-400/20',
      },
      {
        key: 'cartera',
        titulo: 'Saldo Cartera',
        valor: formatCurrency(s.totalCartera),
        icon: DollarSign,
        gradient: 'from-emerald-500 to-green-600',
        iconBg: 'bg-emerald-400/20',
      },
      {
        key: 'mora',
        titulo: 'En Mora',
        valor: s.enMora,
        icon: AlertCircle,
        gradient: 'from-rose-500 to-red-600',
        iconBg: 'bg-rose-400/20',
      },
      {
        key: 'total',
        titulo: 'Total Préstamos',
        valor: s.totalPrestamos,
        icon: BarChart3,
        gradient: 'from-violet-500 to-purple-600',
        iconBg: 'bg-violet-400/20',
      },
    ];
  }, [stats]);

  // Helper para el badge de estado
  const estadoBadge = (estado) => {
    const e = (estado || '').toUpperCase();
    const styles = {
      ACTIVO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      EN_MORA: 'bg-red-50 text-red-700 border-red-200',
      CANCELADO: 'bg-gray-100 text-gray-500 border-gray-200',
      PAGADO: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return styles[e] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  // Helper para nombre del socio
  const labelSocio = (p) => {
    return p.socioNombre || p.socioNombreCompleto ||
      (p.socio?.nombreCompleto) ||
      (p.socio ? `${p.socio.nombres || ''} ${p.socio.apellidos || ''}`.trim() : '') ||
      `Socio #${p.socioId || p.idSocio || '?'}`;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Cartera de Préstamos</h1>
          <p className="text-sm text-gray-500">Visualización y gestión de la cartera crediticia</p>
        </div>
        <button
          onClick={fetchStats}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Error */}
      {errorMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {errorMsg}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loadingStats
          ? Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl p-4 animate-pulse bg-gray-100 h-24" />
          ))
          : statsCards.map((stat) => (
            <div
              key={stat.key}
              className={`relative overflow-hidden rounded-xl p-4 bg-gradient-to-br ${stat.gradient} text-white shadow-lg shadow-${stat.key === 'mora' ? 'red' : stat.key === 'activos' ? 'blue' : stat.key === 'cartera' ? 'green' : 'purple'}-500/20 hover:shadow-xl transition-shadow`}
            >
              {/* Decorative circle */}
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10" />

              <div className="relative flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-white/80 mb-1">{stat.titulo}</p>
                  <p className="text-xl font-bold tracking-tight">{stat.valor}</p>
                </div>
                <div className={`${stat.iconBg} p-2 rounded-lg`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Tabla de Cartera */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-gray-800">Listado de Préstamos</h2>
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
              {prestamosFiltrados.length}
            </span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar socio o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-56 pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
              />
            </div>

            {/* Filter */}
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
            >
              <option value="TODOS">Todos</option>
              <option value="ACTIVO">Activos</option>
              <option value="EN_MORA">En Mora</option>
              <option value="CANCELADO">Cancelados</option>
              <option value="PAGADO">Pagados</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Préstamo</th>
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Socio</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Monto Original</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Saldo</th>
                <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plazo</th>
                <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingPrestamos ? (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-gray-400">Cargando...</td></tr>
              ) : prestamosFiltrados.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-gray-400">No hay préstamos para mostrar</td></tr>
              ) : (
                prestamosFiltrados.map((p) => (
                  <tr
                    key={p.idPrestamo || p.id}
                    className="hover:bg-gray-50/80 transition-colors cursor-pointer group"
                    onClick={() => setSelectedPrestamo(p)}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center flex-shrink-0">
                          <Landmark className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-800">#{p.idPrestamo || p.id}</div>
                          <div className="text-xs text-gray-400">{formatDate(p.fechaDesembolso || p.creadoAt)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-medium text-gray-800">{labelSocio(p)}</div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="text-sm font-semibold text-gray-800">{formatCurrency(p.montoOriginal)}</div>
                    </td>
                    <td className="p-3 text-right">
                      <div className={`text-sm font-semibold ${(p.estado || '').toUpperCase() === 'EN_MORA' ? 'text-red-600' : 'text-gray-700'}`}>
                        {formatCurrency(p.saldoPendiente)}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-sm text-gray-600">{p.plazoMeses || p.plazo || '—'} m</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${estadoBadge(p.estado)}`}>
                        {p.estado || '—'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer stats */}
        {prestamosFiltrados.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-xs text-gray-500">
            <span>Mostrando {prestamosFiltrados.length} de {prestamos.length} préstamos</span>
            <span className="font-medium">
              Saldo total filtrado: {formatCurrency(prestamosFiltrados.reduce((s, p) => s + (p.saldoPendiente || 0), 0))}
            </span>
          </div>
        )}
      </div>

      {/* Detalle Modal */}
      {selectedPrestamo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-5 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-green-100 mb-1">Préstamo #{selectedPrestamo.idPrestamo || selectedPrestamo.id}</p>
                  <h3 className="text-lg font-bold">{labelSocio(selectedPrestamo)}</h3>
                </div>
                <button
                  onClick={() => setSelectedPrestamo(null)}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Monto Original</p>
                  <p className="text-base font-bold text-gray-900">{formatCurrency(selectedPrestamo.montoOriginal)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Saldo Pendiente</p>
                  <p className={`text-base font-bold ${(selectedPrestamo.estado || '').toUpperCase() === 'EN_MORA' ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCurrency(selectedPrestamo.saldoPendiente)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Plazo</p>
                  <p className="text-base font-bold text-gray-900">{selectedPrestamo.plazoMeses || selectedPrestamo.plazo || '—'} meses</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Tasa Anual</p>
                  <p className="text-base font-bold text-gray-900">
                    {selectedPrestamo.tasaInteresAnual != null
                      ? `${(Number(selectedPrestamo.tasaInteresAnual) * 100).toFixed(1)}%`
                      : '—'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Desembolso</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(selectedPrestamo.fechaDesembolso)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Estado</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${estadoBadge(selectedPrestamo.estado)}`}>
                    {selectedPrestamo.estado || '—'}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              {selectedPrestamo.montoOriginal > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Progreso de pago</span>
                    <span className="font-medium">
                      {Math.round(((selectedPrestamo.montoOriginal - (selectedPrestamo.saldoPendiente || 0)) / selectedPrestamo.montoOriginal) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(((selectedPrestamo.montoOriginal - (selectedPrestamo.saldoPendiente || 0)) / selectedPrestamo.montoOriginal) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedPrestamo(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
